import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle2, XCircle, Store, Star, Loader2, Power, Plus, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useCountry, COUNTRIES } from "@/lib/country-context";
import { useToast } from "@/hooks/use-toast";
import adminApi from "@/lib/admin-api";

const TABS = [
  { key: "pending", label: "Pending Approval" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "", label: "All" },
];

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function AdminRestaurantsPage() {
  const { toast } = useToast();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab) params.approval = tab;
      if (search) params.search = search;
      const { data } = await adminApi.get("/restaurants", { params });
      setRestaurants(data);
    } catch {
      toast({ title: "Couldn't load restaurants", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [tab, search, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-bold">Restaurants</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Review applications and manage active restaurants.</p>
        </div>
        <Button className="gap-2" onClick={() => setCreating(true)} data-testid="admin-add-restaurant">
          <Plus className="w-4 h-4" /> Add restaurant
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search name, owner, cuisine, phone..." className="pl-9 h-10"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-restaurants-search" />
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button key={t.key || "all"} onClick={() => setTab(t.key)}
            data-testid={`admin-restaurants-tab-${t.key || "all"}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
        </div>
      ) : restaurants.length === 0 ? (
        <div className="bg-card border rounded-2xl p-12 text-center text-muted-foreground">
          <Store className="w-12 h-12 mx-auto opacity-20 mb-3" />
          <p className="font-medium">No restaurants in this view</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {restaurants.map((r) => (
            <div key={r.id} onClick={() => setSelectedId(r.id)}
              className="bg-card border rounded-2xl p-4 hover:shadow-sm cursor-pointer transition-all"
              data-testid={`admin-restaurant-row-${r.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden shrink-0">
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                  ) : <div className="w-full h-full flex items-center justify-center"><Store className="w-5 h-5 text-muted-foreground/40" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{r.name}</h3>
                    <Badge className={`text-[10px] font-semibold border ${STATUS_STYLE[r.approval_status] || ""}`}>
                      {r.approval_status}
                    </Badge>
                    {!r.is_active && r.approval_status === "approved" && (
                      <Badge className="text-[10px] bg-muted text-muted-foreground border">suspended</Badge>
                    )}
                    {r.is_featured && (
                      <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20">featured</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {r.cuisine} • {r.owner_name} • {r.phone}
                  </p>
                  {r.address && <p className="text-xs text-muted-foreground">{r.address}</p>}
                </div>
                <div className="text-right shrink-0 text-xs text-muted-foreground hidden sm:block">
                  <p className="flex items-center gap-1 justify-end"><Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {r.rating?.toFixed?.(1)}</p>
                  <p className="mt-1">{r.delivery_time} min • ${r.delivery_fee?.toFixed?.(2)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedId && (
        <RestaurantDetailDialog
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}

      {creating && (
        <CreateRestaurantDialog
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); setTab("approved"); load(); }}
        />
      )}
    </div>
  );
}

const CUISINES = ["Ghanaian", "Nigerian", "Nigerian Grills", "Ghanaian Street", "Nigerian Home", "West African", "Continental", "Other"];

function CreateRestaurantDialog({ onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", owner_name: "", country: "GH", phone_local: "",
    password: "", cuisine: "Ghanaian", address: "",
    delivery_time: 30, delivery_fee: 2.99, image_url: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPhoneLocal = (raw) => setForm({ ...form, phone_local: (raw || "").replace(/\D/g, "") });
  const dial = COUNTRIES[form.country].dialCode;

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Image too large (max 2MB)", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone_local.length < 6) {
      toast({ title: "Enter a valid phone number", variant: "destructive" });
      return;
    }
    if (form.password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    const fullPhone = `${dial}${form.phone_local.replace(/^0+/, "")}`;
    setSaving(true);
    try {
      await adminApi.post("/restaurants", {
        name: form.name, owner_name: form.owner_name,
        phone: fullPhone, country: form.country, password: form.password,
        cuisine: form.cuisine, address: form.address || null,
        image_url: form.image_url || null,
        delivery_time: Number(form.delivery_time),
        delivery_fee: Number(form.delivery_fee),
      });
      toast({ title: "Restaurant added", description: "Auto-approved & live in the catalogue." });
      onCreated();
    } catch (e) {
      toast({ title: "Couldn't create", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Add restaurant</DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">Admin-created restaurants are auto-approved and immediately visible to customers.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Restaurant name</Label>
              <Input required value={form.name} onChange={upd("name")} placeholder="Jollof Junction" data-testid="admin-resto-name" />
            </div>
            <div className="space-y-2">
              <Label>Owner name</Label>
              <Input required value={form.owner_name} onChange={upd("owner_name")} placeholder="Owner full name" data-testid="admin-resto-owner" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Country</Label>
            <div className="grid grid-cols-2 gap-2">
              {["GH", "NG"].map((code) => {
                const c = COUNTRIES[code];
                const active = form.country === code;
                return (
                  <button key={code} type="button" onClick={() => setForm({ ...form, country: code })}
                    data-testid={`admin-resto-country-${code}`}
                    className={`flex items-center gap-2 p-2 rounded-lg border-2 text-sm text-left ${
                      active ? "border-primary bg-primary/5" : "border-input"
                    }`}>
                    <span className="text-lg">{c.flag}</span>
                    <span className="font-medium">{c.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{c.dialCode}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Phone</Label>
            <div className="flex items-stretch h-10 rounded-md border border-input bg-background overflow-hidden">
              <div className="flex items-center px-3 bg-muted text-muted-foreground text-sm font-semibold border-r">{dial}</div>
              <input type="tel" inputMode="numeric" required value={form.phone_local}
                onChange={(e) => setPhoneLocal(e.target.value)}
                className="flex-1 bg-transparent px-3 text-sm focus:outline-none"
                placeholder={form.country === "GH" ? "24 123 4567" : "801 234 5678"}
                data-testid="admin-resto-phone" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Cuisine</Label>
              <select value={form.cuisine} onChange={upd("cuisine")}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="admin-resto-cuisine">
                {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={upd("password")}
                placeholder="At least 6 chars" data-testid="admin-resto-password" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={upd("address")} placeholder="12 Oxford St, Osu, Accra"
              data-testid="admin-resto-address" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Delivery time (min)</Label>
              <Input type="number" min="5" value={form.delivery_time} onChange={upd("delivery_time")}
                data-testid="admin-resto-delivery-time" />
            </div>
            <div className="space-y-2">
              <Label>Delivery fee (USD)</Label>
              <Input type="number" step="0.01" min="0" value={form.delivery_fee} onChange={upd("delivery_fee")}
                data-testid="admin-resto-delivery-fee" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cover image (optional)</Label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="" className="w-16 h-16 rounded-md object-cover border" />
              ) : (
                <div className="w-16 h-16 rounded-md border flex items-center justify-center bg-muted">
                  <ImageIcon className="w-6 h-6 text-muted-foreground/40" />
                </div>
              )}
              <Label className="cursor-pointer">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium hover:bg-muted transition-colors">
                  <ImageIcon className="w-3.5 h-3.5" /> Upload
                </span>
                <input type="file" accept="image/*" onChange={handleImage} className="hidden"
                  data-testid="admin-resto-image" />
              </Label>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="w-full font-semibold bg-gradient-primary shadow-glow"
            data-testid="admin-resto-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add restaurant"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RestaurantDetailDialog({ id, onClose, onChanged }) {
  const { formatPrice } = useCountry();
  const { toast } = useToast();
  const [r, setR] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  useEffect(() => {
    adminApi.get(`/restaurants/${id}`).then(({ data }) => setR(data));
  }, [id]);

  const patch = async (payload) => {
    setUpdating(true);
    try {
      const { data } = await adminApi.patch(`/restaurants/${id}`, payload);
      setR((prev) => ({ ...prev, ...data }));
      toast({ title: "Updated" });
      onChanged();
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setUpdating(false); }
  };

  const approve = () => patch({ approval_status: "approved", is_active: true, rejection_reason: null });
  const reject = () => {
    if (!rejectReason.trim()) {
      toast({ title: "Please add a reason", variant: "destructive" });
      return;
    }
    patch({ approval_status: "rejected", is_active: false, rejection_reason: rejectReason.trim() })
      .then(() => { setShowReject(false); setRejectReason(""); });
  };
  const toggleActive = () => patch({ is_active: !r.is_active });
  const toggleFeatured = () => patch({ is_featured: !r.is_featured });

  if (!r) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{r.name}</DialogTitle>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Badge className={`text-xs border ${STATUS_STYLE[r.approval_status] || ""}`}>{r.approval_status}</Badge>
            {!r.is_active && r.approval_status === "approved" && (
              <Badge className="bg-muted text-muted-foreground border">suspended</Badge>
            )}
            {r.is_featured && <Badge className="bg-primary/10 text-primary border-primary/20">featured</Badge>}
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-3">
          {r.image_url && (
            <img src={r.image_url} alt={r.name} className="w-full h-40 object-cover rounded-xl border" />
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Owner">{r.owner_name}</Field>
            <Field label="Phone">{r.phone}</Field>
            <Field label="Country">{r.country}</Field>
            <Field label="Cuisine">{r.cuisine}</Field>
            <Field label="Delivery time">{r.delivery_time} min</Field>
            <Field label="Delivery fee">${r.delivery_fee?.toFixed?.(2)}</Field>
            <Field label="Rating">{r.rating?.toFixed?.(1)}</Field>
            <Field label="Open">{r.is_open ? "Yes" : "No"}</Field>
          </div>

          <Field label="Address">{r.address || "—"}</Field>

          {r.rejection_reason && (
            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-destructive mb-1">Rejection reason</p>
              <p className="text-sm">{r.rejection_reason}</p>
            </div>
          )}

          <div className="bg-muted/30 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-xs">
            <StatBox label="Total orders" value={r.stats?.total_orders ?? 0} />
            <StatBox label="Delivered" value={r.stats?.delivered_orders ?? 0} />
            <StatBox label="Revenue" value={formatPrice(r.stats?.revenue ?? 0)} />
            <StatBox label="Menu items" value={r.stats?.menu_items ?? 0} />
          </div>

          {/* Actions */}
          {r.approval_status === "pending" ? (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button disabled={updating} onClick={approve} className="gap-2" data-testid="admin-restaurant-approve">
                <CheckCircle2 className="w-4 h-4" /> Approve
              </Button>
              <Button disabled={updating} variant="outline" onClick={() => setShowReject(true)}
                className="gap-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                data-testid="admin-restaurant-reject">
                <XCircle className="w-4 h-4" /> Reject
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button variant="outline" disabled={updating} onClick={toggleActive} className="gap-2"
                data-testid="admin-restaurant-toggle-active">
                <Power className="w-4 h-4" /> {r.is_active ? "Suspend" : "Re-activate"}
              </Button>
              <Button variant="outline" disabled={updating} onClick={toggleFeatured} className="gap-2"
                data-testid="admin-restaurant-toggle-featured">
                <Star className="w-4 h-4" /> {r.is_featured ? "Remove featured" : "Make featured"}
              </Button>
            </div>
          )}

          {showReject && (
            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold">Rejection reason</p>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Business documents missing, address could not be verified, etc."
                rows={3} data-testid="admin-restaurant-reject-reason" />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setShowReject(false)}>Cancel</Button>
                <Button size="sm" variant="destructive" disabled={updating || !rejectReason.trim()} onClick={reject}
                  data-testid="admin-restaurant-reject-confirm">Confirm rejection</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm">{children}</p>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-serif font-bold text-base mt-0.5">{value}</p>
    </div>
  );
}
